/**
 * SPCB Checkout Document Module v1.8.0
 *
 * Handles:
 * 1. Document type (Boleta/Factura) radio cards + conditional invoice fields
 * 2. Pre-fill fields from customer preferences (returning customers)
 * 3. Store logo + contact info injection above contact section
 * 4. Policy links footer below checkout actions
 * 5. Error message cleaner - removes "(opcional)" from WC Blocks validation errors
 * 6. Cookie-based persistence for invoice data
 * 7. Clickable products in order summary
 * 8. Main initialization and orchestration
 *
 * @package SuperPymeCheckoutBlocks
 */

(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Document]', ...args);
    const isCheckoutProcessing = () => {
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return true;
        }

        const checkoutStore = window.wp?.data?.select?.('wc/store/checkout');
        return !!(
            checkoutStore?.isProcessing?.() ||
            checkoutStore?.isBeforeProcessing?.() ||
            checkoutStore?.isAfterProcessing?.()
        );
    };

    const timers = (() => {
        const timeouts = new Map();
        const intervals = new Map();
        const pausedTimeouts = [];
        const pausedIntervals = [];
        let paused = false;

        const scheduleTimeout = (fn, delay, label) => {
            if (paused || isCheckoutProcessing()) {
                log('Timeout blocked - checkout processing:', label);
                return null;
            }

            const id = setTimeout(() => {
                timeouts.delete(id);

                if (paused || isCheckoutProcessing()) {
                    log('Timeout skipped - checkout processing:', label);
                    return;
                }

                fn();
            }, delay);

            timeouts.set(id, { fn, delay, label });
            return id;
        };

        const scheduleInterval = (fn, delay, label) => {
            if (paused || isCheckoutProcessing()) {
                log('Interval blocked - checkout processing:', label);
                return null;
            }

            const id = setInterval(() => {
                if (paused || isCheckoutProcessing()) {
                    log('Interval tick skipped - checkout processing:', label);
                    return;
                }

                fn();
            }, delay);

            intervals.set(id, { fn, delay, label });
            return id;
        };

        const clearManagedTimeout = (id) => {
            if (!id) return;
            clearTimeout(id);
            timeouts.delete(id);
        };

        const pause = () => {
            if (paused) return;
            paused = true;

            timeouts.forEach((meta, id) => {
                clearTimeout(id);
                pausedTimeouts.push(meta);
            });
            timeouts.clear();

            intervals.forEach((meta, id) => {
                clearInterval(id);
                pausedIntervals.push(meta);
            });
            intervals.clear();

            log('Timers paused - checkout processing');
        };

        const resume = () => {
            if (!paused) return;
            paused = false;

            while (pausedTimeouts.length) {
                const meta = pausedTimeouts.shift();
                if (meta?.fn) {
                    scheduleTimeout(meta.fn, 0, meta.label);
                }
            }

            while (pausedIntervals.length) {
                const meta = pausedIntervals.shift();
                if (meta?.fn) {
                    scheduleInterval(meta.fn, meta.delay, meta.label);
                }
            }

            log('Timers resumed - checkout processing ended');
        };

        return {
            scheduleTimeout,
            scheduleInterval,
            clearManagedTimeout,
            pause,
            resume
        };
    })();

    if (typeof window.spcbCheckoutLock?.subscribe === 'function') {
        window.spcbCheckoutLock.subscribe((processing) => {
            if (processing) {
                timers.pause();
            } else {
                timers.resume();
            }
        });
    }

    // ============================================
    // REGION NAMES MAPPING (shared constant)
    // ============================================
    const REGION_NAMES = {
        'CL-AP': 'Arica y Parinacota',
        'CL-TA': 'Tarapacá',
        'CL-AN': 'Antofagasta',
        'CL-AT': 'Atacama',
        'CL-CO': 'Coquimbo',
        'CL-VS': 'Valparaíso',
        'CL-RM': 'Región Metropolitana',
        'CL-LI': 'O\'Higgins',
        'CL-ML': 'Maule',
        'CL-NB': 'Ñuble',
        'CL-BI': 'Biobío',
        'CL-AR': 'La Araucanía',
        'CL-LR': 'Los Ríos',
        'CL-LL': 'Los Lagos',
        'CL-AI': 'Aysén',
        'CL-MA': 'Magallanes'
    };

    // Expose REGION_NAMES for other modules
    window.spcbRegionNames = REGION_NAMES;

    // ============================================
    // STORE LOGO
    // ============================================

    let logoInjected = false;

    function injectStoreLogo() {
        if (logoInjected) return;

        const config = window.spcbConfig || {};
        const logoUrl = config.storeLogo;
        const phone = config.storePhone;
        const email = config.storeEmail;

        // Need at least logo, phone or email to show anything
        if (!logoUrl && !phone && !email) {
            log('No branding configured');
            return;
        }

        // Find the contact section (first step of checkout)
        const contactStep = document.querySelector('.wc-block-checkout__contact-fields, .wp-block-woocommerce-checkout-contact-information-block');
        if (!contactStep) {
            log('Contact step not found for logo injection');
            return;
        }

        // Check if already exists
        if (document.querySelector('.spcb-store-header')) {
            logoInjected = true;
            return;
        }

        // Create header container
        const headerContainer = document.createElement('div');
        headerContainer.className = 'spcb-store-header';

        const homeUrl = config.homeUrl || '/';

        // Build HTML
        let html = '';

        // Logo
        if (logoUrl) {
            const logoWidth = config.storeLogoWidth || 200;
            html += `<a href="${homeUrl}" class="spcb-store-logo" title="Ir al inicio"><img src="${logoUrl}" alt="Logo de tienda" style="max-width: ${logoWidth}px;"></a>`;
        }

        // Contact info
        if (phone || email) {
            html += '<div class="spcb-store-contact">';

            if (phone) {
                // Remove + for wa.me link (WhatsApp expects number without +)
                const waNumber = phone.replace(/[^0-9]/g, '');
                // Format phone for display
                const displayPhone = phone.startsWith('+') ? phone : `+${phone}`;
                html += `
                    <a href="https://wa.me/${waNumber}" target="_blank" class="spcb-contact-link spcb-contact-whatsapp" title="Contactar por WhatsApp">
                        <svg class="spcb-contact-icon" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span>${displayPhone}</span>
                    </a>
                `;
            }

            if (email) {
                html += `
                    <a href="mailto:${email}" class="spcb-contact-link spcb-contact-email" title="Enviar correo">
                        <svg class="spcb-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        <span>${email}</span>
                    </a>
                `;
            }

            html += '</div>';
        }

        // Checkout notice (informative text)
        const checkoutNotice = config.checkoutNotice;
        if (checkoutNotice) {
            html += `<div class="spcb-checkout-notice">${checkoutNotice}</div>`;
        }

        headerContainer.innerHTML = html;

        // Insert before the contact step
        contactStep.parentNode.insertBefore(headerContainer, contactStep);
        logoInjected = true;

        log('Store header injected');
    }

    // ============================================
    // POLICY LINKS FOOTER
    // ============================================

    let policyLinksInjected = false;

    function injectPolicyLinksFooter() {
        if (policyLinksInjected) return;

        const config = window.spcbConfig || {};
        const policyLinks = config.policyLinks;

        // Check if we have any policy links configured
        if (!policyLinks) {
            log('No policy links configured');
            return;
        }

        // Filter links that have both text and URL
        const validLinks = [];
        if (policyLinks.refund?.url && policyLinks.refund?.text) {
            validLinks.push(policyLinks.refund);
        }
        if (policyLinks.privacy?.url && policyLinks.privacy?.text) {
            validLinks.push(policyLinks.privacy);
        }
        if (policyLinks.terms?.url && policyLinks.terms?.text) {
            validLinks.push(policyLinks.terms);
        }

        if (validLinks.length === 0) {
            log('No valid policy links found (need both text and URL)');
            return;
        }

        // Find the checkout actions container
        const actionsContainer = document.querySelector('.wc-block-checkout__actions');
        if (!actionsContainer) {
            log('Checkout actions container not found');
            return;
        }

        // Check if already injected
        if (document.querySelector('.spcb-policy-footer')) {
            policyLinksInjected = true;
            return;
        }

        // Create footer container
        const footer = document.createElement('div');
        footer.className = 'spcb-policy-footer';

        // Build links HTML
        const linksHtml = validLinks.map(link =>
            `<a href="${link.url}" target="_blank" rel="noopener noreferrer">${link.text}</a>`
        ).join('<span class="spcb-policy-separator">·</span>');

        footer.innerHTML = `
            <div class="spcb-policy-divider"></div>
            <div class="spcb-policy-links">${linksHtml}</div>
        `;

        // Insert after the actions container
        actionsContainer.insertAdjacentElement('afterend', footer);
        policyLinksInjected = true;

        log('Policy links footer injected');

        // Reorder: Move footer contact BEFORE policy links if it exists
        const footerContact = document.getElementById('spcb-footer-contact');
        if (footerContact && footer.parentNode) {
            footer.parentNode.insertBefore(footerContact, footer);
            footerContact.style.display = 'block';
            log('Footer contact repositioned before policy links');
        }

        // Mobile: Move trust benefits AFTER footer contact, BEFORE policy links
        const isMobile = window.innerWidth <= 782;
        if (isMobile) {
            const trustBenefits = document.getElementById('spcb-trust-benefits');
            if (trustBenefits && footer.parentNode) {
                footer.parentNode.insertBefore(trustBenefits, footer);
                trustBenefits.style.display = 'block';
                log('Trust benefits repositioned before policy links (mobile)');
            }
        }
    }

    // ============================================
    // ENHANCE TERMS BLOCK WITH POLICY LINKS (v1.16.27)
    // ============================================

    let termsEnhanced = false;

    /**
     * Enhance the native WooCommerce terms block by:
     * 1. Adding Refund Policy link at the beginning
     * 2. Converting plain text "Términos y condiciones" to clickable link (if WC pages not configured)
     * 3. Converting plain text "Política de privacidad" to clickable link (if WC pages not configured)
     *
     * This makes SPCB independent of WooCommerce page configuration.
     */
    function enhanceTermsBlock() {
        if (termsEnhanced) return;

        const config = window.spcbConfig || {};
        const policyLinks = config.policyLinks;
        const refundUrl = policyLinks?.refund?.url;
        const termsUrl = policyLinks?.terms?.url;
        const privacyUrl = policyLinks?.privacy?.url;

        // Need at least one URL to enhance
        if (!refundUrl && !termsUrl && !privacyUrl) {
            log('No policy URLs configured for terms enhancement');
            return;
        }

        // Find the native WC terms block label (try multiple selectors)
        let termsBlock = document.querySelector('.wc-block-checkout__terms .wc-block-components-checkbox__label');
        if (!termsBlock) {
            // Fallback: find span inside terms block (non-checkbox mode)
            termsBlock = document.querySelector('.wc-block-checkout__terms > span');
        }
        if (!termsBlock) {
            log('Terms block not found');
            return;
        }

        // Check if already enhanced
        if (termsBlock.querySelector('.spcb-refund-link')) {
            termsEnhanced = true;
            return;
        }

        let html = termsBlock.innerHTML;
        let modified = false;

        // 1. Add refund link at the beginning (if URL configured)
        if (refundUrl) {
            const refundText = policyLinks?.refund?.text || 'Política de reembolsos';
            const refundLink = `<a href="${refundUrl}" target="_blank" class="spcb-refund-link">${refundText}</a>`;

            html = html.replace(
                /Al proceder con tu compra aceptas nuestr[oa]s?\s*/i,
                `Al proceder con tu compra aceptas nuestras ${refundLink}, `
            );
            modified = true;
        }

        // 2. Convert "Términos y condiciones" to link if it's plain text (not already a link)
        // Check if it exists but is NOT inside an <a> tag
        if (termsUrl && html.match(/Términos y condiciones/i) && !html.match(/<a[^>]*>[^<]*Términos y condiciones[^<]*<\/a>/i)) {
            html = html.replace(
                /Términos y condiciones/gi,
                `<a href="${termsUrl}" target="_blank">Términos y condiciones</a>`
            );
            modified = true;
            log('Converted "Términos y condiciones" from plain text to link');
        }

        // 3. Convert "Política de privacidad" to link if it's plain text
        if (privacyUrl && html.match(/Política de privacidad/i) && !html.match(/<a[^>]*>[^<]*Política de privacidad[^<]*<\/a>/i)) {
            html = html.replace(
                /Política de privacidad/gi,
                `<a href="${privacyUrl}" target="_blank">Política de privacidad</a>`
            );
            modified = true;
            log('Converted "Política de privacidad" from plain text to link');
        }

        // Update if something changed
        if (modified) {
            termsBlock.innerHTML = html;
            termsEnhanced = true;
            log('Terms block enhanced with policy links');
        }
    }

    // ============================================
    // INJECT TERMS CHECKBOX (v1.16.30)
    // Instead of using WooCommerce Additional Checkout Fields API (which escapes HTML),
    // we inject our own checkbox with full control over the HTML.
    // ============================================

    function injectTermsCheckbox() {
        const config = window.spcbConfig || {};

        // Only if the option is enabled
        if (!config.forceTermsCheckbox) return;

        // Already injected?
        if (document.querySelector('.spcb-terms-checkbox-injected')) return;

        // Hide native WC terms block
        const nativeTerms = document.querySelector('.wc-block-checkout__terms');
        if (nativeTerms) {
            nativeTerms.style.display = 'none';
        }

        // Find insertion point (before the place order button)
        const actionsBlock = document.querySelector('.wc-block-checkout__actions');
        if (!actionsBlock) return;

        // Get policy URLs from config
        const policyLinks = config.policyLinks || {};
        const termsUrl = policyLinks.terms?.url || '#';
        const privacyUrl = policyLinks.privacy?.url || '#';
        const refundUrl = policyLinks.refund?.url || '';

        // Build links array with refund first (if exists)
        const links = [];
        if (refundUrl) {
            links.push(`<a href="${refundUrl}" target="_blank" rel="noopener">Política de Reembolsos</a>`);
        }
        links.push(`<a href="${termsUrl}" target="_blank" rel="noopener">Términos y Condiciones</a>`);
        links.push(`<a href="${privacyUrl}" target="_blank" rel="noopener">Política de Privacidad</a>`);

        // Join with proper connectors: "A, B y C" or "A y B"
        let linksHtml;
        if (links.length === 3) {
            linksHtml = `${links[0]}, ${links[1]} y ${links[2]}`;
        } else {
            linksHtml = links.join(' y ');
        }

        // Build checkbox HTML
        const checkboxHtml = `
            <div class="spcb-terms-checkbox-injected">
                <label class="spcb-terms-label">
                    <input type="checkbox" id="spcb-terms-checkbox" class="spcb-terms-input" required>
                    <span class="spcb-terms-text">He leído y acepto ${linksHtml}.</span>
                </label>
                <div class="spcb-terms-error" style="display: none;">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 24 24" width="20" height="20" fill="currentColor">
                        <path d="M10 2c4.42 0 8 3.58 8 8s-3.58 8-8 8-8-3.58-8-8 3.58-8 8-8zm1.13 9.38l.35-6.46H8.52l.35 6.46h2.26zm-.09 3.36c.24-.23.37-.55.37-.96 0-.42-.12-.74-.36-.97s-.59-.35-1.06-.35-.82.12-1.07.35-.37.55-.37.97c0 .41.13.73.38.96.26.23.61.34 1.06.34s.8-.11 1.05-.34z"></path>
                    </svg>
                    <span>Debes aceptar los términos para continuar.</span>
                </div>
            </div>
        `;

        // Insert before the actions block
        actionsBlock.insertAdjacentHTML('beforebegin', checkboxHtml);

        // Setup validation
        const checkbox = document.getElementById('spcb-terms-checkbox');
        const errorDiv = document.querySelector('.spcb-terms-error');

        if (!checkbox || !errorDiv) return;

        // Intercept click on place order button (capture phase to run before WC)
        const placeOrderBtn = document.querySelector('.wc-block-components-checkout-place-order-button');
        if (placeOrderBtn) {
            placeOrderBtn.addEventListener('click', (e) => {
                if (!checkbox.checked) {
                    e.preventDefault();
                    e.stopPropagation();
                    errorDiv.style.display = 'flex';
                    checkbox.focus();

                    // Scroll to checkbox
                    checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, true); // capture phase
        }

        // Hide error when checkbox is checked
        checkbox.addEventListener('change', () => {
            if (checkbox.checked) {
                errorDiv.style.display = 'none';
            }
        });

        log('Terms checkbox injected successfully');
    }

    // ============================================
    // ORDER NOTES CHARACTER LIMIT (v1.16.27)
    // ============================================

    let orderNotesObserverSetup = false;

    /**
     * Apply character limit to order notes textarea
     * Called when textarea is found/created
     */
    function applyOrderNotesLimit(textarea) {
        const config = window.spcbConfig || {};
        const maxChars = config.orderNotesMaxChars || 100;

        // Already limited?
        if (textarea.hasAttribute('maxlength')) {
            return;
        }

        // Add maxlength attribute
        textarea.setAttribute('maxlength', maxChars);

        // Create counter element
        const counter = document.createElement('div');
        counter.className = 'spcb-order-notes-counter';
        counter.style.cssText = 'text-align: right; font-size: 12px; color: #666; margin-top: 4px;';

        const updateCounter = () => {
            const remaining = maxChars - textarea.value.length;
            counter.textContent = `${textarea.value.length}/${maxChars}`;
            counter.style.color = remaining < 20 ? '#cc1818' : '#666';
        };

        // Insert counter after textarea
        textarea.parentNode.insertBefore(counter, textarea.nextSibling);

        // Update on input
        textarea.addEventListener('input', updateCounter);

        // Initial update
        updateCounter();

        log(`Order notes limited to ${maxChars} characters`);
    }

    /**
     * Setup observer for order notes textarea
     * The textarea appears dynamically when user checks "Add note" checkbox
     */
    function limitOrderNotes() {
        if (orderNotesObserverSetup) return;

        const config = window.spcbConfig || {};

        // Check if feature is enabled
        if (!config.orderNotesLimitEnabled) {
            log('Order notes limit not enabled');
            return;
        }

        // Find the order notes container
        const orderNotesBlock = document.querySelector('.wc-block-checkout__add-note');
        if (!orderNotesBlock) {
            log('Order notes block not found');
            return;
        }

        // Check if textarea already exists (checkbox already checked)
        const existingTextarea = orderNotesBlock.querySelector('textarea.wc-block-components-textarea');
        if (existingTextarea && !existingTextarea.hasAttribute('maxlength')) {
            applyOrderNotesLimit(existingTextarea);
        }

        // Setup MutationObserver to detect when textarea is added
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if added node is the textarea
                        if (node.matches && node.matches('textarea.wc-block-components-textarea')) {
                            applyOrderNotesLimit(node);
                        }
                        // Or check if textarea is inside added node
                        const textarea = node.querySelector && node.querySelector('textarea.wc-block-components-textarea');
                        if (textarea && !textarea.hasAttribute('maxlength')) {
                            applyOrderNotesLimit(textarea);
                        }
                    }
                }
            }
        });

        observer.observe(orderNotesBlock, {
            childList: true,
            subtree: true
        });

        orderNotesObserverSetup = true;
        log('Order notes observer setup complete');
    }

    // ============================================
    // MOVE YITH POINTS MESSAGE (v1.16.27)
    // ============================================

    let yithMoved = false;

    /**
     * Move YITH Points message to correct position in checkout
     *
     * Target position: After checkout notice (yellow banner), before contact info
     * This is necessary because CSS order doesn't work (YITH is not in a flex container)
     */
    function moveYithPointsMessage() {
        if (yithMoved) return;

        const yithMessage = document.querySelector('.wp-block-yith-par-message-reward-cart_container');
        if (!yithMessage) {
            return; // YITH not active or message not rendered yet
        }

        // Check if already moved (inside store header area)
        const storeHeader = document.querySelector('.spcb-store-header');
        if (storeHeader && storeHeader.contains(yithMessage)) {
            yithMoved = true;
            return;
        }

        // Target: after .spcb-checkout-notice (if exists) or after .spcb-store-header
        const checkoutNotice = document.querySelector('.spcb-checkout-notice');
        const contactFields = document.querySelector('.wc-block-checkout__contact-fields');

        // Determine insertion point
        let insertAfter = checkoutNotice || storeHeader;

        if (!insertAfter && contactFields) {
            // Insert before contact fields if no header/notice
            contactFields.parentNode.insertBefore(yithMessage, contactFields);
            yithMoved = true;
            log('YITH Points message moved before contact fields');
            return;
        }

        if (insertAfter) {
            // Insert after notice or header
            insertAfter.parentNode.insertBefore(yithMessage, insertAfter.nextSibling);
            yithMoved = true;
            log('YITH Points message moved after', insertAfter.className);
        }
    }

    // ============================================
    // CLICKABLE PRODUCTS IN ORDER SUMMARY
    // ============================================

    /**
     * Make products clickable in checkout order summary
     * Opens product page in new tab when clicking name or image
     */
    function initClickableProducts() {
        // Use event delegation on document to handle dynamically added items
        document.addEventListener('click', (e) => {
            // Check if click is on product name
            const productName = e.target.closest('.wc-block-components-product-name');
            if (productName) {
                const permalink = productName.dataset.permalink || productName.getAttribute('href');
                if (permalink) {
                    e.preventDefault();
                    window.open(permalink, '_blank');
                    return;
                }
                // If no permalink in data, try to find from cart data
                const orderItem = productName.closest('.wc-block-components-order-summary-item');
                if (orderItem) {
                    const productUrl = getProductUrlFromItem(orderItem);
                    if (productUrl) {
                        e.preventDefault();
                        window.open(productUrl, '_blank');
                        return;
                    }
                }
            }

            // Check if click is on product image
            const productImage = e.target.closest('.wc-block-components-order-summary-item__image');
            if (productImage) {
                const orderItem = productImage.closest('.wc-block-components-order-summary-item');
                if (orderItem) {
                    // Try to get URL from product name first
                    const nameEl = orderItem.querySelector('.wc-block-components-product-name');
                    const permalink = nameEl?.dataset.permalink || nameEl?.getAttribute('href');
                    if (permalink) {
                        e.preventDefault();
                        window.open(permalink, '_blank');
                        return;
                    }
                    // Fallback to computed URL
                    const productUrl = getProductUrlFromItem(orderItem);
                    if (productUrl) {
                        e.preventDefault();
                        window.open(productUrl, '_blank');
                        return;
                    }
                }
            }
        });

        log('Clickable products initialized');
    }

    /**
     * Try to get product URL from order item element
     * Prioritizes variation permalink over parent product URL
     */
    function getProductUrlFromItem(orderItem) {
        const nameEl = orderItem.querySelector('.wc-block-components-product-name');

        // Method 1: Get variation permalink from WooCommerce cart store
        // This ensures we link to the specific variation, not the parent product
        try {
            const wpData = window.wp?.data;
            if (wpData?.select) {
                const cartStore = wpData.select('wc/store/cart');
                const cartData = cartStore?.getCartData?.();
                const items = cartData?.items || [];

                if (items.length > 0 && nameEl) {
                    const productName = nameEl.textContent?.trim();

                    // Try to match by product name
                    const matchedItem = items.find(item => {
                        const itemName = item.name || '';
                        return itemName === productName;
                    });

                    if (matchedItem) {
                        // For variations, use variation permalink if available
                        // Otherwise use the regular permalink
                        const permalink = matchedItem.permalink || matchedItem.product_permalink;
                        if (permalink) {
                            log('Got variation permalink from cart store:', permalink);
                            return permalink;
                        }
                    }

                    // If no exact match, try matching by item key from DOM
                    const itemKey = orderItem.querySelector('[data-item-key]')?.dataset?.itemKey ||
                                   orderItem.querySelector('.spcb-qty-control')?.dataset?.itemKey;
                    if (itemKey) {
                        const itemByKey = items.find(item => item.key === itemKey);
                        if (itemByKey?.permalink) {
                            log('Got permalink by item key:', itemByKey.permalink);
                            return itemByKey.permalink;
                        }
                    }
                }
            }
        } catch (e) {
            log('Error getting permalink from cart store:', e.message);
        }

        // Method 2: Check if product name has data-permalink (WooCommerce default)
        if (nameEl) {
            const permalink = nameEl.dataset.permalink || nameEl.getAttribute('href');
            if (permalink) return permalink;
        }

        // Method 3: Try to build URL from product name (slugify) - last resort fallback
        if (nameEl) {
            const productName = nameEl.textContent?.trim();
            if (productName) {
                const slug = productName
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '') // Remove accents
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '');
                // Use window location to build product URL
                const baseUrl = window.location.origin;
                return `${baseUrl}/producto/${slug}/`;
            }
        }

        return null;
    }

    // ============================================
    // CUSTOMER PREFERENCES (PRE-FILL FOR RETURNING CUSTOMERS)
    // ============================================

    function getPreferences() {
        return window.spcbPreferences || {};
    }

    function prefillFieldsFromPreferences() {
        const prefs = getPreferences();
        if (!prefs || Object.keys(prefs).length === 0) {
            log('No preferences found for pre-filling');
            return;
        }

        log('Pre-filling fields from preferences:', prefs);

        // Pre-fill document type
        if (prefs.document_type) {
            prefillDocumentType(prefs.document_type);
        }

        // Pre-fill RUT
        if (prefs.billing_rut) {
            prefillField('billing_rut', prefs.billing_rut);
        }

        // Pre-fill invoice fields if exists
        if (prefs.invoice_company) {
            prefillField('invoice_company', prefs.invoice_company);
        }
        if (prefs.invoice_giro) {
            prefillField('invoice_giro', prefs.invoice_giro);
        }
        if (prefs.invoice_address) {
            prefillField('invoice_address', prefs.invoice_address);
        }
        if (prefs.invoice_phone) {
            prefillField('invoice_phone', prefs.invoice_phone);
        }

        // Pre-fill region and comuna (with delay for select population)
        if (prefs.invoice_region) {
            prefillRegionComuna(prefs.invoice_region, prefs.invoice_comuna || '');
        }

        // Pre-fill other transport data to localStorage if exists
        if (prefs.last_other_transport) {
            const otherData = window.spcbShipping?.getOtherData?.();
            if (!otherData || !otherData.transportName) {
                // Only set if no existing other data
                const newOtherData = {
                    transportName: prefs.last_other_transport
                };
                localStorage.setItem('spcb_other_data', JSON.stringify(newOtherData));
                log('Other transport preferences restored to localStorage');
            }
        }
    }

    function prefillDocumentType(value) {
        if (isCheckoutProcessing()) {
            log('Prefill document type blocked - checkout processing');
            return;
        }

        // v1.16.0: If React DocumentTypeBlock is active, it handles preferences
        // via spcbPreferences which is set before the block mounts
        if (document.querySelector('.spcb-document-type-block')) {
            log('Document type prefill handled by React block');
            // The React block reads from window.spcbPreferences on mount
            // Just update the body class for immediate visual feedback
            updateDocumentTypeClass(value);
            return;
        }

        // Legacy fallback: Wait for select to be available
        const tryPrefill = () => {
            const select = findDocumentTypeSelect();
            if (!select) {
                log('Document type select not found yet, retrying...');
                return false;
            }

            // Set the hidden select value
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));

            // Update radio cards if already transformed
            const radio = document.querySelector(`input[name="spcb-document-type-radio"][value="${value}"]`);
            if (radio) {
                radio.checked = true;
            }

            updateDocumentTypeClass(value);
            log('Pre-filled document type:', value);
            return true;
        };

        // Try immediately, then with delays if needed
        if (!tryPrefill()) {
            timers.scheduleTimeout(tryPrefill, 500, 'prefill-document-type');
            timers.scheduleTimeout(tryPrefill, 1500, 'prefill-document-type');
        }
    }

    function prefillField(fieldName, value) {
        if (isCheckoutProcessing()) {
            log('Prefill field blocked - checkout processing:', fieldName);
            return false;
        }

        // Find input by data attribute
        const input = document.querySelector(`[data-spcb-field="${fieldName}"] input, [data-spcb-field="${fieldName}"] textarea, input[data-spcb-field="${fieldName}"]`);
        if (input && !input.value) {
            input.value = value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            log('Pre-filled field:', fieldName, value);
            return true;
        }
        return false;
    }

    function prefillRegionComuna(regionCode, comunaName) {
        if (isCheckoutProcessing()) {
            log('Prefill region/comuna blocked - checkout processing');
            return;
        }

        const prefillRegion = () => {
            // Make sure we're in Factura mode before trying to prefill
            if (!document.body.classList.contains('spcb-factura-mode')) {
                log('Not in Factura mode yet, skipping region prefill');
                return false;
            }

            // Find region select - try both selector patterns (attribute on select or on parent)
            let regionSelect = document.querySelector('select[data-spcb-field="invoice_region"]');
            if (!regionSelect) {
                regionSelect = document.querySelector('[data-spcb-field="invoice_region"] select');
            }

            if (!regionSelect) {
                log('Region select not found for prefill');
                return false;
            }

            // Check if select is disabled (invoice fields hidden)
            if (regionSelect.disabled) {
                log('Region select is disabled, waiting...');
                return false;
            }

            // Check if the option exists
            const optionExists = Array.from(regionSelect.options).some(opt => opt.value === regionCode);
            if (!optionExists) {
                log('Region option not found:', regionCode);
                return false;
            }

            if (regionSelect.value === regionCode) {
                // Already set
                log('Region already set to:', regionCode);
                // Still try to set comuna
                if (comunaName) {
                    timers.scheduleTimeout(() => prefillComuna(comunaName), 200, 'prefill-comuna');
                }
                return true;
            }

            // Set region
            regionSelect.value = regionCode;
            regionSelect.dispatchEvent(new Event('change', { bubbles: true }));
            log('Pre-filled invoice region:', regionCode);

            // Set comuna after a delay (to allow dropdown to populate)
            if (comunaName) {
                timers.scheduleTimeout(() => prefillComuna(comunaName), 300, 'prefill-comuna');
            }

            return true;
        };

        const prefillComuna = (comunaName) => {
            // Find comuna select - try both patterns
            let comunaSelect = document.querySelector('select[data-spcb-field="invoice_comuna"]');
            if (!comunaSelect) {
                comunaSelect = document.querySelector('[data-spcb-field="invoice_comuna"] select');
            }

            if (comunaSelect && !comunaSelect.disabled) {
                // Check if option exists
                const optionExists = Array.from(comunaSelect.options).some(opt => opt.value === comunaName);
                if (optionExists) {
                    comunaSelect.value = comunaName;
                    comunaSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    log('Pre-filled invoice comuna:', comunaName);
                } else {
                    log('Comuna option not found:', comunaName);
                }
            }
        };

        // Try with longer delays to ensure Factura mode is active and fields are rendered
        // The document type prefill uses 500ms and 1500ms, so we use longer delays
        timers.scheduleTimeout(prefillRegion, 1000, 'prefill-region');
        timers.scheduleTimeout(prefillRegion, 2000, 'prefill-region');
        timers.scheduleTimeout(prefillRegion, 3000, 'prefill-region'); // Extra retry for slow connections
    }

    // ============================================
    // INVOICE DATA COOKIE PERSISTENCE
    // ============================================

    /**
     * Save invoice data to cookie for reliable persistence
     * Called when invoice fields change (region, comuna, etc.)
     */
    function saveInvoiceDataToCookie() {
        try {
            // Only save if in Factura mode
            if (!document.body.classList.contains('spcb-factura-mode')) {
                return;
            }

            const regionSelect = document.querySelector('select[data-spcb-field="invoice_region"]') ||
                                 document.querySelector('[data-spcb-field="invoice_region"] select');
            const comunaSelect = document.querySelector('select[data-spcb-field="invoice_comuna"]') ||
                                 document.querySelector('[data-spcb-field="invoice_comuna"] select');
            const companyInput = document.querySelector('input[data-spcb-field="invoice_company"]') ||
                                 document.querySelector('[data-spcb-field="invoice_company"] input');
            const giroInput = document.querySelector('input[data-spcb-field="invoice_giro"]') ||
                              document.querySelector('[data-spcb-field="invoice_giro"] input');
            const addressInput = document.querySelector('input[data-spcb-field="invoice_address"]') ||
                                 document.querySelector('[data-spcb-field="invoice_address"] input');
            const phoneInput = document.querySelector('input[data-spcb-field="invoice_phone"]') ||
                               document.querySelector('[data-spcb-field="invoice_phone"] input');

            const invoiceData = {
                region: regionSelect?.value || '',
                comuna: comunaSelect?.value || '',
                company: companyInput?.value || '',
                giro: giroInput?.value || '',
                address: addressInput?.value || '',
                phone: phoneInput?.value || '',
            };

            // Only save if we have at least region or company
            if (!invoiceData.region && !invoiceData.company) {
                return;
            }

            const cookieValue = encodeURIComponent(JSON.stringify(invoiceData));
            const expiryDate = new Date();
            expiryDate.setTime(expiryDate.getTime() + (60 * 60 * 1000)); // 1 hour

            document.cookie = `spcb_invoice_data=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
            log('Invoice data saved to cookie:', invoiceData.region, invoiceData.comuna);
        } catch (e) {
            console.error('[SPCB] Failed to save invoice data to cookie:', e);
        }
    }

    /**
     * Initialize invoice data cookie sync
     * Listens for changes on invoice fields and saves to cookie
     */
    function initInvoiceDataCookieSync() {
        const invoiceFields = [
            'invoice_region',
            'invoice_comuna',
            'invoice_company',
            'invoice_giro',
            'invoice_address',
            'invoice_phone'
        ];

        invoiceFields.forEach(fieldName => {
            const field = document.querySelector(`select[data-spcb-field="${fieldName}"]`) ||
                          document.querySelector(`input[data-spcb-field="${fieldName}"]`) ||
                          document.querySelector(`[data-spcb-field="${fieldName}"] select`) ||
                          document.querySelector(`[data-spcb-field="${fieldName}"] input`);

            if (field) {
                field.addEventListener('change', () => {
                    // Small delay to ensure value is updated
                    timers.scheduleTimeout(saveInvoiceDataToCookie, 100, 'invoice-cookie-save');
                });
                field.addEventListener('blur', () => {
                    timers.scheduleTimeout(saveInvoiceDataToCookie, 100, 'invoice-cookie-save');
                });
            }
        });

        // Also save when document type changes to Factura
        document.addEventListener('change', (e) => {
            if (e.target.name === 'spcb-document-type-radio' && e.target.value === 'factura') {
                timers.scheduleTimeout(saveInvoiceDataToCookie, 500, 'invoice-cookie-save');
            }
        });

        log('Invoice data cookie sync initialized');
    }

    // ============================================
    // INVOICE COMUNA FILTERING BY REGION
    // ============================================

    // v1.11.10: Use sessionStorage for persistence across React re-renders and page interactions
    // This is more robust than just a JS variable which can be lost
    const INVOICE_REGION_STORAGE_KEY = 'spcb_invoice_region_selected';

    // Also keep in-memory for faster access
    let _spcbSelectedInvoiceRegion = sessionStorage.getItem(INVOICE_REGION_STORAGE_KEY) || '';

    /**
     * Save invoice region value to both memory and sessionStorage
     * v1.11.10: Dual storage for maximum persistence
     */
    function saveInvoiceRegionValue(value) {
        _spcbSelectedInvoiceRegion = value;
        if (value) {
            sessionStorage.setItem(INVOICE_REGION_STORAGE_KEY, value);
        } else {
            sessionStorage.removeItem(INVOICE_REGION_STORAGE_KEY);
        }
        log('Invoice region saved:', value);
    }

    /**
     * Get the saved invoice region value
     */
    function getSavedInvoiceRegion() {
        // Prefer memory, fallback to sessionStorage
        if (_spcbSelectedInvoiceRegion) {
            return _spcbSelectedInvoiceRegion;
        }
        return sessionStorage.getItem(INVOICE_REGION_STORAGE_KEY) || '';
    }

    /**
     * Filter invoice comunas based on selected region
     * v1.11.10: Complete rewrite with more robust persistence mechanism
     */
    function initInvoiceComunaFiltering() {
        const regionSelect = document.querySelector('select[data-spcb-field="invoice_region"]') ||
                             document.querySelector('[data-spcb-field="invoice_region"] select');

        if (!regionSelect) {
            log('Invoice region select not found for filtering');
            return;
        }

        // Restore saved value on init
        const savedRegion = getSavedInvoiceRegion();
        if (savedRegion && regionSelect.value !== savedRegion) {
            log('Restoring saved invoice region on init:', savedRegion);
            forceSelectValue(regionSelect, savedRegion);
        }

        // v1.17.7: SIEMPRE cargar comunas filtradas si hay región (guardada o ya establecida)
        // Esto resuelve el bug donde el select de comunas mostraba TODAS las comunas
        // al cargar el checkout con datos previos guardados
        const currentRegion = savedRegion || regionSelect.value;
        if (currentRegion) {
            log('Loading comunas for current/saved region:', currentRegion);
            // Usar timeout para permitir que forceSelectValue termine (200ms para _spcbRestoring)
            timers.scheduleTimeout(async () => {
                try {
                    let comunas = [];
                    if (window.spcbComunaLoader && window.spcbComunaLoader.loadComunas) {
                        comunas = await window.spcbComunaLoader.loadComunas(currentRegion);
                    } else {
                        const response = await fetch(`/wp-json/spcb/v1/comunas/${currentRegion}`);
                        const data = await response.json();
                        comunas = data.comunas || [];
                    }
                    if (comunas.length > 0) {
                        updateInvoiceComunaOptions(comunas);
                        log('Comunas filtered on init for region:', currentRegion, '- count:', comunas.length);
                    }
                } catch (err) {
                    log('Error loading comunas on init:', err);
                }
            }, 250, 'init-filter-comunas');
        }

        // Check if already initialized
        if (regionSelect._spcbComunaFilterListener) {
            return;
        }

        // v1.11.10: Use event delegation on the container for more reliability
        const container = regionSelect.closest('.wc-block-components-select-input') || regionSelect.parentElement;

        regionSelect.addEventListener('change', async (e) => {
            // v1.11.10: Skip if this is our restoration event
            if (regionSelect._spcbRestoring) {
                log('Skipping change event - restoration in progress');
                return;
            }

            const regionCode = e.target.value;
            log('Invoice region changed to:', regionCode);

            // Save immediately
            saveInvoiceRegionValue(regionCode);

            if (!regionCode) {
                return;
            }

            // Load comunas
            let comunas = [];
            try {
                if (window.spcbComunaLoader && window.spcbComunaLoader.loadComunas) {
                    comunas = await window.spcbComunaLoader.loadComunas(regionCode);
                } else {
                    const response = await fetch(`/wp-json/spcb/v1/comunas/${regionCode}`);
                    const data = await response.json();
                    comunas = data.comunas || [];
                }
            } catch (err) {
                log('Error loading comunas:', err);
                return;
            }

            updateInvoiceComunaOptions(comunas);

            // v1.11.10: Force restore after comuna update (React may have reset it)
            timers.scheduleTimeout(() => {
                const currentRegion = getSavedInvoiceRegion();
                if (currentRegion && regionSelect.value !== currentRegion) {
                    log('Region was reset after comuna update, restoring:', currentRegion);
                    forceSelectValue(regionSelect, currentRegion);
                }
            }, 50, 'invoice-region-restore');
        });

        regionSelect._spcbComunaFilterListener = true;
        log('Invoice comuna filtering initialized');

        // Set up observer and periodic check
        initInvoiceRegionObserver();
        initInvoiceRegionPeriodicCheck();
    }

    /**
     * Force a select element to have a specific value
     * v1.11.10: Uses multiple techniques to ensure the value is set
     */
    function forceSelectValue(selectElement, value) {
        if (isCheckoutProcessing()) {
            log('forceSelectValue blocked - checkout processing:', value);
            return false;
        }

        if (!selectElement || !value) return false;

        // Check if option exists
        const optionExists = Array.from(selectElement.options).some(opt => opt.value === value);
        if (!optionExists) {
            log('Cannot set select value - option not found:', value);
            return false;
        }

        // Already has the correct value
        if (selectElement.value === value) {
            return true;
        }

        selectElement._spcbRestoring = true;

        try {
            // Method 1: Direct assignment
            selectElement.value = value;

            // Method 2: Native setter (bypasses React/Vue proxies)
            try {
                const nativeSetter = Object.getOwnPropertyDescriptor(
                    HTMLSelectElement.prototype, 'value'
                ).set;
                nativeSetter.call(selectElement, value);
            } catch (e) { }

            // Method 3: Set selectedIndex
            const optionIndex = Array.from(selectElement.options).findIndex(opt => opt.value === value);
            if (optionIndex >= 0) {
                selectElement.selectedIndex = optionIndex;
            }

            // Dispatch events to notify frameworks
            selectElement.dispatchEvent(new Event('input', { bubbles: true }));
            selectElement.dispatchEvent(new Event('change', { bubbles: true }));

            log('Forced invoice region value to:', value, '(current:', selectElement.value, ')');
        } finally {
            timers.scheduleTimeout(() => {
                selectElement._spcbRestoring = false;
            }, 200, 'invoice-region-restore-flag');
        }

        return selectElement.value === value;
    }

    /**
     * Restore the invoice region select value
     */
    function restoreInvoiceRegionValue(selectElement) {
        const savedRegion = getSavedInvoiceRegion();
        if (!savedRegion) return;

        const regionSelect = selectElement ||
            document.querySelector('select[data-spcb-field="invoice_region"]') ||
            document.querySelector('[data-spcb-field="invoice_region"] select');

        if (!regionSelect) return;

        if (regionSelect.value !== savedRegion) {
            forceSelectValue(regionSelect, savedRegion);
        }
    }

    /**
     * v1.11.10: Periodic check to ensure region value persists
     * This is a fallback for when MutationObserver misses changes
     */
    let _invoiceRegionCheckInterval = null;
    function initInvoiceRegionPeriodicCheck() {
        // Don't create multiple intervals
        if (_invoiceRegionCheckInterval) return;

        _invoiceRegionCheckInterval = timers.scheduleInterval(() => {
            // Only check if we're in Factura mode
            if (!document.body.classList.contains('spcb-factura-mode')) return;

            const savedRegion = getSavedInvoiceRegion();
            if (!savedRegion) return;

            const regionSelect = document.querySelector('select[data-spcb-field="invoice_region"]') ||
                                document.querySelector('[data-spcb-field="invoice_region"] select');

            if (regionSelect && regionSelect.value !== savedRegion && !regionSelect._spcbRestoring) {
                log('Periodic check: region was reset, restoring:', savedRegion);
                forceSelectValue(regionSelect, savedRegion);
            }
        }, 500, 'invoice-region-polling'); // Check every 500ms

        log('Invoice region periodic check initialized');
    }

    /**
     * MutationObserver to detect when React re-renders the region field
     * v1.11.10: Simplified and more robust
     */
    function initInvoiceRegionObserver() {
        const invoiceContainer = document.querySelector('.wc-block-checkout__contact-fields') ||
                                 document.querySelector('.wp-block-woocommerce-checkout-contact-information-block');

        if (!invoiceContainer) {
            log('Invoice container not found for observer');
            return;
        }

        // Don't create multiple observers
        if (invoiceContainer._spcbRegionObserver) return;

        let restoreTimeout = null;

        const observer = new MutationObserver((mutations) => {
            // Only act if we're in Factura mode
            if (!document.body.classList.contains('spcb-factura-mode')) return;

            const savedRegion = getSavedInvoiceRegion();
            if (!savedRegion) return;

            // Debounce
            timers.clearManagedTimeout(restoreTimeout);
            restoreTimeout = timers.scheduleTimeout(() => {
                const regionSelect = document.querySelector('select[data-spcb-field="invoice_region"]') ||
                                    document.querySelector('[data-spcb-field="invoice_region"] select');

                if (regionSelect && regionSelect.value !== savedRegion && !regionSelect._spcbRestoring) {
                    log('MutationObserver: region was reset, restoring:', savedRegion);
                    forceSelectValue(regionSelect, savedRegion);

                    // Re-init listener if needed
                    if (!regionSelect._spcbComunaFilterListener) {
                        initInvoiceComunaFiltering();
                    }
                }
            }, 100, 'invoice-region-observer');
        });

        observer.observe(invoiceContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['value', 'selected']
        });

        invoiceContainer._spcbRegionObserver = observer;
        log('Invoice region observer initialized');
    }

    /**
     * Update invoice comuna select options with filtered comunas
     */
    function updateInvoiceComunaOptions(comunas) {
        const comunaSelect = document.querySelector('select[data-spcb-field="invoice_comuna"]') ||
                             document.querySelector('[data-spcb-field="invoice_comuna"] select');

        if (!comunaSelect) {
            log('Invoice comuna select not found');
            return;
        }

        const currentValue = comunaSelect.value;

        // Clear existing options
        comunaSelect.innerHTML = '';

        // Add placeholder
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Seleccionar comuna';
        placeholder.dataset.alternateValues = '[Seleccionar un comuna factura]';
        comunaSelect.appendChild(placeholder);

        // Add filtered comunas
        let hasCurrentValue = false;
        comunas.forEach(comuna => {
            const option = document.createElement('option');
            option.value = comuna;
            option.textContent = comuna;
            option.dataset.alternateValues = `[${comuna}]`;
            if (comuna === currentValue) {
                option.selected = true;
                hasCurrentValue = true;
            }
            comunaSelect.appendChild(option);
        });

        // If current value is not in the new list, reset selection
        if (!hasCurrentValue) {
            placeholder.selected = true;
            comunaSelect.value = '';
        }

        // Update searchable-select display if present
        const searchableContainer = comunaSelect.previousElementSibling;
        if (searchableContainer?.classList.contains('spcb-searchable-select')) {
            const valueDisplay = searchableContainer.querySelector('.spcb-searchable-select__value');
            if (valueDisplay) {
                if (hasCurrentValue) {
                    valueDisplay.textContent = currentValue;
                    valueDisplay.classList.remove('is-placeholder');
                } else {
                    valueDisplay.textContent = 'Seleccionar comuna';
                    valueDisplay.classList.add('is-placeholder');
                }
            }
        }

        // Dispatch event to notify other components (with checkout guard)
        if (!window.spcbCheckoutLock?.isProcessing?.()) {
            comunaSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        log('Invoice comuna options updated:', comunas.length, 'comunas');
    }

    // ============================================
    // DOCUMENT TYPE (BOLETA/FACTURA)
    // ============================================

    function findDocumentTypeSelect() {
        const byData = document.querySelector('[data-spcb-field="document_type"] select');
        if (byData) return byData;

        const allSelects = document.querySelectorAll('select');
        for (const select of allSelects) {
            const options = Array.from(select.options).map(o => o.value.toLowerCase());
            if (options.includes('boleta') || options.includes('factura')) {
                return select;
            }
        }

        return document.querySelector('select[id*="document_type"], select[id*="document-type"]');
    }

    function updateDocumentTypeClass(value) {
        document.body.classList.remove('spcb-boleta-mode', 'spcb-factura-mode');
        document.body.classList.add(value === 'factura' ? 'spcb-factura-mode' : 'spcb-boleta-mode');
        toggleInvoiceFields(value === 'factura');

        // v1.10.31: Actualizar texto de ayuda del billing_rut según tipo de documento
        // Pasamos 'value' directamente para evitar problemas de timing con el DOM
        const billingRutInput = document.querySelector('input[data-spcb-field="billing_rut"]');
        if (billingRutInput && window.spcbUpdateRutHelpText) {
            window.spcbUpdateRutHelpText(billingRutInput, 'billing_rut', value);
        }

        log('Document type:', value);
    }

    /**
     * Toggle invoice fields visibility and state
     * v1.11.10: NO longer adds 'required' attribute to avoid premature browser/WC Blocks validation
     * The actual required validation is handled by backend on checkout submission
     */
    function toggleInvoiceFields(enable) {
        const optionalText = ' (opcional)';

        document.querySelectorAll('[data-spcb-conditional="factura"]').forEach(field => {
            const inputs = field.tagName === 'INPUT' || field.tagName === 'SELECT' || field.tagName === 'TEXTAREA'
                ? [field]
                : field.querySelectorAll('input, select, textarea');

            inputs.forEach(input => {
                input.disabled = !enable;

                // Find the label and field wrapper for this field
                const fieldWrapper = input.closest('.wc-block-components-text-input, .wc-block-components-select-input');
                const label = fieldWrapper?.querySelector('label');

                if (enable) {
                    // v1.11.10: DO NOT set HTML5 required attribute
                    // This was causing premature validation by browser and WC Blocks
                    // The backend will validate required fields on checkout submission
                    // input.setAttribute('required', ''); // REMOVED - causes premature validation

                    // Only add visual indicator class (CSS shows asterisk or border)
                    fieldWrapper?.classList.add('spcb-field-required');

                    // Remove "(opcional)" from label text completely
                    if (label) {
                        const baseText = label.dataset.spcbBaseLabel || label.textContent.replace(optionalText, '').trim();
                        label.dataset.spcbBaseLabel = baseText;
                        label.textContent = baseText;
                    }

                    // Also update SELECT placeholder option (first option with empty value)
                    if (input.tagName === 'SELECT') {
                        const placeholderOption = input.querySelector('option[value=""]');
                        if (placeholderOption) {
                            const baseOptionText = placeholderOption.dataset.spcbBaseText || placeholderOption.textContent.replace(optionalText, '').trim();
                            placeholderOption.dataset.spcbBaseText = baseOptionText;
                            placeholderOption.textContent = baseOptionText;
                        }
                    }
                } else {
                    // Remove required (in case it was set before this update)
                    input.removeAttribute('required');
                    fieldWrapper?.classList.remove('spcb-field-required');

                    // Add "(opcional)" back to label
                    if (label) {
                        const baseText = label.dataset.spcbBaseLabel || label.textContent.replace(optionalText, '').trim();
                        label.dataset.spcbBaseLabel = baseText;
                        label.textContent = baseText + optionalText;
                    }

                    // Also update SELECT placeholder option
                    if (input.tagName === 'SELECT') {
                        const placeholderOption = input.querySelector('option[value=""]');
                        if (placeholderOption) {
                            const baseOptionText = placeholderOption.dataset.spcbBaseText || placeholderOption.textContent.replace(optionalText, '').trim();
                            placeholderOption.dataset.spcbBaseText = baseOptionText;
                            placeholderOption.textContent = baseOptionText + optionalText;
                        }
                    }

                    // Note: Don't manipulate DOM for errors - let WC Blocks/React handle it
                    // We'll clear the validation store after the loop
                }
            });
        });

        // When switching to Boleta, clear WC Blocks validation store (API oficial)
        // This is CRITICAL - DOM cleanup alone doesn't clear React/wp.data state
        if (!enable) {
            clearWCBlocksValidationErrors();
        }

        log('Invoice fields', enable ? 'enabled (visual required)' : 'disabled (optional)');
    }

    /**
     * Clear validation error for a specific field and force WC Blocks to re-validate
     * WC Blocks uses React state, so we need to trigger events to update validation
     */
    function clearFieldValidationError(fieldWrapper, input) {
        if (!fieldWrapper) return;

        // Remove error class from wrapper
        fieldWrapper.classList.remove('has-error');

        // Find and remove error message elements
        const errorSelectors = [
            '.wc-block-components-validation-error',
            '.wc-block-components-text-input__error',
            '.wc-block-components-select-input__error',
            '[class*="__error"]'
        ];

        errorSelectors.forEach(selector => {
            const errorEl = fieldWrapper.querySelector(selector);
            if (errorEl) {
                errorEl.remove();
            }
        });

        // Also check parent for error state
        const parent = fieldWrapper.parentElement;
        if (parent?.classList.contains('has-error')) {
            parent.classList.remove('has-error');
        }

        // Force WC Blocks to re-validate by triggering events
        // WC Blocks ignores events on disabled inputs, so temporarily enable
        if (input) {
            const wasDisabled = input.disabled;
            input.disabled = false;

            // Small delay to ensure DOM is updated, then trigger events
            timers.scheduleTimeout(() => {
                if (isCheckoutProcessing()) {
                    if (wasDisabled) {
                        input.disabled = true;
                    }
                    return;
                }

                // Dispatch events to trigger WC Blocks validation update
                input.dispatchEvent(new Event('focus', { bubbles: true }));
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));

                // Re-disable after events
                if (wasDisabled) {
                    input.disabled = true;
                }
            }, 10, 'validation-recheck');
        }

        log('Cleared validation error and triggered re-validation');
    }

    /**
     * Clear validation errors from WC Blocks internal store (API oficial)
     * Solo quitar el DOM no es suficiente - WC Blocks usa React/wp.data
     *
     * Note: The validation store API varies between WooCommerce Blocks versions.
     * We check for method existence to maintain compatibility.
     */
    function clearWCBlocksValidationErrors() {
        try {
            const wpData = window.wp?.data;
            if (!wpData?.dispatch || !wpData?.select) {
                log('wp.data not available');
                return;
            }

            // Use store name directly (more reliable than wcBlocksData reference)
            const STORE_NAME = 'wc/store/validation';

            // Check if the validation store exists and has the required methods
            const validationStore = wpData.select(STORE_NAME);
            if (!validationStore) {
                log('Validation store not available');
                return;
            }

            // getValidationErrors may not exist in older WC Blocks versions
            if (typeof validationStore.getValidationErrors !== 'function') {
                log('getValidationErrors not available (older WC Blocks version)');
                return;
            }

            // First, see what errors exist
            const currentErrors = validationStore.getValidationErrors();
            log('Current validation errors:', currentErrors);

            if (!currentErrors || Object.keys(currentErrors).length === 0) {
                log('No validation errors to clear');
                return;
            }

            // Clear each error by its actual key
            const dispatchStore = wpData.dispatch(STORE_NAME);
            if (!dispatchStore || typeof dispatchStore.clearValidationError !== 'function') {
                log('clearValidationError not available');
                return;
            }

            Object.keys(currentErrors).forEach(errorKey => {
                log('Clearing error:', errorKey);
                dispatchStore.clearValidationError(errorKey);
            });

            // Verify they were cleared
            const remainingErrors = validationStore.getValidationErrors();
            log('Remaining errors after clear:', remainingErrors);

        } catch (e) {
            log('Validation store compatibility issue:', e.message);
        }
    }

    /**
     * Prepare invoice field labels by storing base text (without "(opcional)")
     * This runs once on page load to prepare labels for toggle
     */
    function prepareInvoiceLabels() {
        const optionalText = ' (opcional)';

        // Find all invoice field labels and store their base text
        document.querySelectorAll('[data-spcb-conditional="factura"]').forEach(field => {
            const fieldWrapper = field.closest('.wc-block-components-text-input, .wc-block-components-select-input');
            if (!fieldWrapper) return;

            const label = fieldWrapper.querySelector('label');
            if (!label || label.dataset.spcbProcessed) return;

            const currentText = label.textContent || '';
            // Store the base text without "(opcional)" for later use
            const baseText = currentText.replace(optionalText, '').trim();
            label.dataset.spcbBaseLabel = baseText;
            label.dataset.spcbProcessed = 'true';
        });

        log('Invoice labels prepared');
    }

    // ============================================
    // ERROR MESSAGE CLEANER
    // ============================================

    let errorMessageObserver = null;

    function cleanErrorMessage(element) {
        if (!element || !element.textContent) return;

        const optionalPattern = /\s*\(opcional\)/gi;
        if (optionalPattern.test(element.textContent)) {
            element.textContent = element.textContent.replace(optionalPattern, '');
            log('Cleaned error message:', element.textContent);
        }
    }

    function initErrorMessageObserver() {
        if (errorMessageObserver) return;

        // Find the checkout form to observe
        const checkoutForm = document.querySelector('.wc-block-checkout, .wp-block-woocommerce-checkout');
        if (!checkoutForm) {
            log('Checkout form not found for error observer');
            return;
        }

        errorMessageObserver = new MutationObserver((mutations) => {
            // Only clean if we're in factura mode
            if (!document.body.classList.contains('spcb-factura-mode')) return;

            for (const mutation of mutations) {
                // Check added nodes for error messages
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;

                    // WC Blocks error message selectors
                    const errorSelectors = [
                        '.wc-block-components-validation-error',
                        '.wc-block-components-text-input__error',
                        '.wc-block-components-select-input__error',
                        '[class*="validation-error"]',
                        '[class*="__error"]'
                    ];

                    // Check if the node itself is an error
                    for (const selector of errorSelectors) {
                        if (node.matches?.(selector)) {
                            cleanErrorMessage(node);
                        }
                    }

                    // Check children for errors
                    const errorElements = node.querySelectorAll?.(errorSelectors.join(', '));
                    if (errorElements) {
                        errorElements.forEach(cleanErrorMessage);
                    }
                }

                // Also check for text changes in existing error elements
                if (mutation.type === 'characterData' && mutation.target.parentElement) {
                    const parent = mutation.target.parentElement;
                    if (parent.className?.includes?.('error') || parent.className?.includes?.('validation')) {
                        cleanErrorMessage(parent);
                    }
                }
            }
        });

        errorMessageObserver.observe(checkoutForm, {
            childList: true,
            subtree: true,
            characterData: true
        });

        log('Error message observer initialized');
    }

    // ============================================
    // RADIO CARDS TRANSFORMATION
    // v1.16.0: This is now DEPRECATED in favor of the React DocumentTypeBlock
    // The function is kept for backwards compatibility but will be skipped
    // if the React block is already mounted
    // ============================================

    function transformSelectToRadioCards(select) {
        if (!select || select.dataset.spcbTransformed === 'true') return;

        // v1.16.0: Skip transformation if React DocumentTypeBlock is already mounted
        // The React block is the primary implementation now
        if (document.querySelector('.spcb-document-type-block')) {
            log('Skipping DOM transformation - React DocumentTypeBlock is active');
            // Hide the original select wrapper
            const wrapper = select.closest('.wc-block-components-select-input');
            if (wrapper) {
                wrapper.style.display = 'none';
                wrapper.dataset.spcbHidden = 'true';
            }
            return;
        }

        const wrapper = select.closest('.wc-block-components-select-input, .wc-block-components-form-row');
        if (!wrapper) return;

        const options = Array.from(select.options)
            .filter(opt => opt.value)
            .map(opt => ({ value: opt.value, label: opt.textContent || opt.value, selected: opt.selected }));

        if (options.length === 0) return;

        const icons = {
            boleta: `<svg class="spcb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>`,
            factura: `<svg class="spcb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <path d="M9 15l2 2 4-4"></path>
            </svg>`
        };

        const descriptions = { boleta: 'Para personas naturales', factura: 'Para empresas con RUT' };

        const radioContainer = document.createElement('div');
        radioContainer.className = 'spcb-radio-cards';
        radioContainer.setAttribute('role', 'radiogroup');
        radioContainer.setAttribute('aria-label', 'Tipo de documento');

        options.forEach(opt => {
            const card = document.createElement('div');
            card.className = 'spcb-radio-card';

            const inputId = `spcb-doc-type-${opt.value}`;
            card.innerHTML = `
                <input type="radio" name="spcb-document-type-radio" id="${inputId}" value="${opt.value}" ${opt.selected ? 'checked' : ''}>
                <label for="${inputId}">
                    ${icons[opt.value] || ''}
                    <span class="spcb-label-text">${opt.label}</span>
                    <span class="spcb-label-desc">${descriptions[opt.value] || ''}</span>
                </label>
            `;

            card.querySelector('input').addEventListener('change', function() {
                if (this.checked) {
                    select.value = this.value;
                    // v1.15.2: Guard to prevent dispatch during checkout processing
                    if (!window.spcbCheckoutLock?.isProcessing?.()) {
                        select.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    updateDocumentTypeClass(this.value);
                }
            });

            radioContainer.appendChild(card);
        });

        select.style.cssText = 'position:absolute;opacity:0;height:0;width:0;overflow:hidden;pointer-events:none;';
        select.setAttribute('tabindex', '-1');
        select.setAttribute('aria-hidden', 'true');

        const selectLabel = wrapper.querySelector('label:not(.spcb-radio-label)');
        if (selectLabel) selectLabel.style.display = 'none';

        select.parentNode.insertBefore(radioContainer, select);
        select.dataset.spcbTransformed = 'true';
        wrapper.classList.add('spcb-document-type-wrapper');

        log('Select transformed to radio cards');
    }

    function initDocumentType() {
        // v1.16.0: Check if document-type module already handled transformation
        // The new module creates .spcb-radio-cards directly
        if (document.querySelector('.spcb-radio-cards') || document.querySelector('.spcb-document-type-block')) {
            log('Document type already transformed, skipping legacy init');
            return;
        }

        const select = findDocumentTypeSelect();
        if (!select || select.dataset.spcbInitialized === 'true' || select.dataset.spcbTransformed === 'true') return;

        transformSelectToRadioCards(select);
        updateDocumentTypeClass(select.value || 'boleta');
        select.addEventListener('change', (e) => updateDocumentTypeClass(e.target.value));
        select.dataset.spcbInitialized = 'true';
        log('Document type initialized (legacy mode)');
    }

    // ============================================
    // ENFORCE CONTACT FIELD LAYOUT (v1.11.2)
    // ============================================
    //
    // PROBLEMA:
    // Algunos temas (como SublimaCHile) tienen CSS que sobrescribe nuestros
    // estilos de layout para los campos de contacto, causando que cada campo
    // aparezca en su propia fila en lugar de 2 por fila.
    //
    // SOLUCIÓN:
    // Aplicar estilos inline directamente a los elementos usando propiedades
    // individuales (NO cssText) para no conflictuar con otros scripts como
    // phone-input.js que también aplican estilos inline.
    //
    function enforceContactFieldLayout() {
        const contactContainer = document.getElementById('contact');
        if (!contactContainer) return;

        // Detectar si estamos en móvil (< 600px)
        const isMobile = window.innerWidth < 600;

        // v1.16.4: Gaps separados para mantener anchos originales
        // column-gap: 16px (mantiene calc(50% - 8px) para campos lado a lado)
        // row-gap: 20px desktop, 15px mobile (más espacio entre filas)
        const rowGap = isMobile ? '15px' : '20px';
        const columnGap = '16px';
        const halfWidth = 'calc(50% - 8px)'; // 50% - (columnGap / 2)

        // Aplicar flex al contenedor usando propiedades individuales
        contactContainer.style.setProperty('display', 'flex', 'important');
        contactContainer.style.setProperty('flex-wrap', 'wrap', 'important');
        contactContainer.style.setProperty('row-gap', rowGap, 'important');
        contactContainer.style.setProperty('column-gap', columnGap, 'important');
        contactContainer.style.setProperty('align-items', 'flex-start', 'important');

        // Helper para aplicar estilos de 50% width a un campo
        function applyHalfWidthStyles(field, order) {
            if (!field) return;
            field.style.setProperty('flex', `0 0 ${halfWidth}`, 'important');
            field.style.setProperty('max-width', halfWidth, 'important');
            field.style.setProperty('width', halfWidth, 'important');
            field.style.setProperty('box-sizing', 'border-box', 'important');
            field.style.setProperty('min-width', '0', 'important');
            field.style.setProperty('order', order, 'important');
            field.style.setProperty('margin', '0', 'important');
        }

        // Helper para aplicar estilos de 100% width a un campo (para móvil)
        function applyFullWidthStyles(field, order) {
            if (!field) return;
            field.style.setProperty('flex', '0 0 100%', 'important');
            field.style.setProperty('max-width', '100%', 'important');
            field.style.setProperty('width', '100%', 'important');
            field.style.setProperty('box-sizing', 'border-box', 'important');
            field.style.setProperty('min-width', '0', 'important');
            field.style.setProperty('order', order, 'important');
            field.style.setProperty('margin', '0', 'important');
        }

        // Aplicar a cada campo
        const firstName = contactContainer.querySelector('.wc-block-components-address-form__spcb-contact_first_name');
        const lastName = contactContainer.querySelector('.wc-block-components-address-form__spcb-contact_last_name');
        const email = contactContainer.querySelector('.wc-block-components-address-form__email');
        const phoneShadow = contactContainer.querySelector('.spcb-phone-shadow[data-shadow-for="contact-spcb-contact_phone"]');

        // Nombre y Apellido siempre al 50%
        applyHalfWidthStyles(firstName, '-3');
        applyHalfWidthStyles(lastName, '-2');

        // Email y Teléfono: 100% en móvil, 50% en desktop
        if (isMobile) {
            applyFullWidthStyles(email, '-1');
            applyFullWidthStyles(phoneShadow, '0');
        } else {
            applyHalfWidthStyles(email, '-1');
            applyHalfWidthStyles(phoneShadow, '0');
        }

        log('Contact field layout enforced via inline styles (individual properties, isMobile=' + isMobile + ')');
    }

    // ============================================
    // FIX REGION PLACEHOLDER TEXT (v1.10.33)
    // ============================================
    //
    // PROBLEMA:
    // WooCommerce traduce "Select a state (optional)" incorrectamente como
    // "Seleccionar un región (opcional)" - dos errores:
    //   1. "un" debería ser "una" (región es femenino)
    //   2. "(opcional)" no debería aparecer - Región es requerida para envíos en Chile
    //
    // POR QUÉ NO FUNCIONA PHP (gettext):
    // WooCommerce Blocks renderiza los selects del lado del cliente con React/JavaScript.
    // Esto ocurre DESPUÉS de que PHP procesa las traducciones, por lo que los filtros
    // gettext no pueden interceptar el texto que React genera dinámicamente.
    //
    // SOLUCIÓN:
    // Usar JavaScript con MutationObserver para detectar cuando React renderiza
    // los selects y corregir el texto directamente en el DOM.
    //
    // ============================================

    /**
     * Corrige el texto del placeholder en selects de Región
     *
     * Busca todos los selects de región (envío y factura) y corrige:
     * - "Seleccionar un región (opcional)" → "Seleccionar una región"
     * - "Seleccionar un región" → "Seleccionar una región"
     * - Variantes con "Selecciona" en lugar de "Seleccionar"
     */
    function fixRegionPlaceholder() {
        // Patrones incorrectos a corregir (case-insensitive)
        const incorrectPatterns = [
            { pattern: /Seleccionar un región \(opcional\)/gi, replacement: 'Seleccionar una región' },
            { pattern: /Seleccionar un región/gi, replacement: 'Seleccionar una región' },
            { pattern: /Selecciona un región \(opcional\)/gi, replacement: 'Selecciona una región' },
            { pattern: /Selecciona un región/gi, replacement: 'Selecciona una región' },
        ];

        // Selectores para encontrar todos los selects de región:
        // - .wc-block-components-address-form__state: select de región en dirección de envío
        // - [data-spcb-field="invoice_region"]: select de región en campos de factura
        const regionSelects = document.querySelectorAll(
            '.wc-block-components-address-form__state select, ' +
            'select[data-spcb-field="invoice_region"], ' +
            '[data-spcb-field="invoice_region"] select'
        );

        regionSelects.forEach(select => {
            if (!select) return;

            // Revisar cada opción del select (especialmente la primera que es el placeholder)
            Array.from(select.options).forEach(option => {
                let text = option.textContent || '';
                let changed = false;

                // Aplicar cada patrón de corrección
                incorrectPatterns.forEach(({ pattern, replacement }) => {
                    if (pattern.test(text)) {
                        text = text.replace(pattern, replacement);
                        changed = true;
                    }
                });

                if (changed) {
                    option.textContent = text;
                    log('Fixed region placeholder:', text);
                }
            });
        });
    }

    /**
     * Inicializa el observador para corregir placeholders de Región
     *
     * WooCommerce Blocks usa React que re-renderiza los selects dinámicamente.
     * Usamos MutationObserver para detectar estos cambios y volver a aplicar
     * la corrección cada vez que React actualiza el DOM.
     */
    function initRegionPlaceholderObserver() {
        // Corregir inmediatamente al cargar
        fixRegionPlaceholder();

        // También corregir con delays para cubrir re-renders de React
        timers.scheduleTimeout(fixRegionPlaceholder, 300, 'region-placeholder-fix');
        timers.scheduleTimeout(fixRegionPlaceholder, 800, 'region-placeholder-fix');
        timers.scheduleTimeout(fixRegionPlaceholder, 1500, 'region-placeholder-fix');

        // Observar cambios dinámicos en el formulario de checkout
        const checkoutForm = document.querySelector('.wc-block-checkout, .wp-block-woocommerce-checkout');
        if (!checkoutForm) return;

        const observer = new MutationObserver((mutations) => {
            let needsFix = false;

            // Revisar si algún nodo agregado contiene selects u opciones
            for (const mutation of mutations) {
                if (mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Detectar si se agregó un select o una opción
                            if (node.matches?.('option') ||
                                node.querySelector?.('option') ||
                                node.matches?.('select') ||
                                node.querySelector?.('select')) {
                                needsFix = true;
                                break;
                            }
                        }
                    }
                }
                if (needsFix) break;
            }

            if (needsFix) {
                // Pequeño delay para que React termine de renderizar
                timers.scheduleTimeout(fixRegionPlaceholder, 50, 'region-placeholder-fix');
            }
        });

        observer.observe(checkoutForm, {
            childList: true,
            subtree: true
        });

        log('Region placeholder observer initialized');
    }

    // ============================================
    // FIELD PLACEHOLDERS (v1.16.18)
    // WC 8.6+ doesn't support placeholder in attributes, so we add them via JS
    // ============================================

    /**
     * v1.16.18: Add placeholders and inputMode via JS
     * WooCommerce 8.6+ doesn't support 'placeholder', 'type', or 'maxlength' in attributes
     *
     * v1.16.26: Removed placeholders from invoice fields (giro, address, phone)
     * to fix floating label conflict in WC Blocks. Only birthday keeps placeholder
     * because the format "DD/MM/AAAA" is critical info the label doesn't provide.
     */
    function initFieldPlaceholders() {
        // v1.16.26: Solo birthday mantiene placeholder (formato es información crítica)
        // Campos de factura sin placeholder para compatibilidad con floating labels
        const placeholders = {
            'birthday': 'DD/MM/AAAA'
        };

        // Campos que necesitan teclado numérico en móvil (reemplazo de type="tel")
        const telFields = ['contact_phone', 'invoice_phone'];

        let placeholdersAdded = 0;

        Object.entries(placeholders).forEach(([field, placeholder]) => {
            const wrapper = document.querySelector(`[data-spcb-field="${field}"]`);
            const input = wrapper?.querySelector('input') || document.querySelector(`input[data-spcb-field="${field}"]`);
            if (input && !input.placeholder) {
                input.placeholder = placeholder;
                placeholdersAdded++;
            }
        });

        // Agregar inputMode="tel" para campos de teléfono (UX móvil)
        telFields.forEach(field => {
            const wrapper = document.querySelector(`[data-spcb-field="${field}"]`);
            const input = wrapper?.querySelector('input');
            if (input && !input.inputMode) {
                input.inputMode = 'tel';
            }
        });

        // Agregar maxLength al campo birthday
        const birthdayWrapper = document.querySelector('[data-spcb-field="birthday"]');
        const birthdayInput = birthdayWrapper?.querySelector('input');
        if (birthdayInput && !birthdayInput.maxLength) {
            birthdayInput.maxLength = 10;
        }

        if (placeholdersAdded > 0) {
            log(`Added ${placeholdersAdded} placeholders via JS`);
        }
    }

    // ============================================
    // MAIN INITIALIZATION
    // ============================================

    function init() {
        log('Initializing SPCB Document Module v1.11.2');

        // Run immediately, no delay needed
        initDocumentType();

        // v1.11.2: Enforce contact field layout with inline styles (fallback for themes)
        timers.scheduleTimeout(enforceContactFieldLayout, 100, 'contact-layout');
        timers.scheduleTimeout(enforceContactFieldLayout, 500, 'contact-layout');
        timers.scheduleTimeout(enforceContactFieldLayout, 1500, 'contact-layout');

        // Initialize shipping module (from checkout-shipping.js)
        if (window.spcbShipping?.initBranchShipping) {
            window.spcbShipping.initBranchShipping();
        }
        if (window.spcbShipping?.initCheckoutValidation) {
            window.spcbShipping.initCheckoutValidation();
        }

        // v1.7.0: Login banner + modal for non-logged-in users
        if (window.spcbAuth?.initLoginBanner) {
            window.spcbAuth.initLoginBanner();
        }

        // v1.7.0: Registration enhancement with visual banner
        if (window.spcbAuth?.initRegistrationEnhancement) {
            window.spcbAuth.initRegistrationEnhancement();
        }

        // v1.7.0: Birthday date picker (with retries for field rendering)
        if (window.spcbAuth?.initBirthdayDatepicker) {
            timers.scheduleTimeout(window.spcbAuth.initBirthdayDatepicker, 500, 'birthday-datepicker');
            timers.scheduleTimeout(window.spcbAuth.initBirthdayDatepicker, 1000, 'birthday-datepicker');
            timers.scheduleTimeout(window.spcbAuth.initBirthdayDatepicker, 2000, 'birthday-datepicker');
        }

        // Prepare invoice labels by storing base text (must run after fields render)
        timers.scheduleTimeout(prepareInvoiceLabels, 100, 'invoice-labels');
        timers.scheduleTimeout(prepareInvoiceLabels, 500, 'invoice-labels');  // Retry for late-rendering fields
        timers.scheduleTimeout(prepareInvoiceLabels, 1500, 'invoice-labels'); // Final retry

        // v1.16.18: Add placeholders via JS (WC 8.6+ doesn't support placeholder in attributes)
        timers.scheduleTimeout(initFieldPlaceholders, 400, 'field-placeholders');
        timers.scheduleTimeout(initFieldPlaceholders, 1200, 'field-placeholders'); // Retry for late-rendering

        // Initialize error message observer (cleans "(opcional)" from error messages)
        timers.scheduleTimeout(initErrorMessageObserver, 200, 'error-message-observer');

        // Pre-fill fields from customer preferences (with delay for fields to render)
        timers.scheduleTimeout(prefillFieldsFromPreferences, 300, 'prefill-preferences');
        timers.scheduleTimeout(prefillFieldsFromPreferences, 1000, 'prefill-preferences');

        // Inject store logo
        injectStoreLogo();
        timers.scheduleTimeout(injectStoreLogo, 500, 'store-logo'); // Retry in case DOM not ready

        // Show shipping time message (retry for late-rendering radios)
        if (window.spcbShipping?.showShippingTimeMessage) {
            timers.scheduleTimeout(window.spcbShipping.showShippingTimeMessage, 300, 'shipping-time-message');
            timers.scheduleTimeout(window.spcbShipping.showShippingTimeMessage, 800, 'shipping-time-message');
            timers.scheduleTimeout(window.spcbShipping.showShippingTimeMessage, 1500, 'shipping-time-message');
        }

        // Initialize "Por Pagar" shipping labels (hides "Gratis" for non-pickup $0 methods)
        if (window.spcbShipping?.initShippingPriceObserver) {
            timers.scheduleTimeout(window.spcbShipping.initShippingPriceObserver, 300, 'shipping-price-observer');
            timers.scheduleTimeout(window.spcbShipping.initShippingPriceObserver, 1000, 'shipping-price-observer');
        }

        // Inject policy links footer
        timers.scheduleTimeout(injectPolicyLinksFooter, 300, 'policy-links');
        timers.scheduleTimeout(injectPolicyLinksFooter, 1000, 'policy-links'); // Retry in case actions not rendered

        // v1.16.27: Enhance terms block with refund policy link
        timers.scheduleTimeout(enhanceTermsBlock, 500, 'terms-enhance');
        timers.scheduleTimeout(enhanceTermsBlock, 1500, 'terms-enhance');

        // v1.16.27: Limit order notes characters
        timers.scheduleTimeout(limitOrderNotes, 500, 'order-notes-limit');
        timers.scheduleTimeout(limitOrderNotes, 1500, 'order-notes-limit');

        // v1.16.27: Move YITH Points message to correct position
        timers.scheduleTimeout(moveYithPointsMessage, 600, 'yith-move');
        timers.scheduleTimeout(moveYithPointsMessage, 1500, 'yith-move');
        timers.scheduleTimeout(moveYithPointsMessage, 3000, 'yith-move');

        // v1.16.27: Add body class for forced terms checkbox
        const config = window.spcbConfig || {};
        if (config.forceTermsCheckbox) {
            document.body.classList.add('spcb-force-terms-checkbox');
            // v1.16.30: Inject our own checkbox (WC API escapes HTML, so we bypass it)
            timers.scheduleTimeout(injectTermsCheckbox, 500, 'terms-inject');
            timers.scheduleTimeout(injectTermsCheckbox, 1500, 'terms-inject');
        }

        // Initialize shipping info hints (with "Más info" modal)
        if (window.spcbShipping?.initShippingInfoHints) {
            window.spcbShipping.initShippingInfoHints();
        }

        // Initialize clickable products in order summary
        initClickableProducts();

        // Initialize invoice data cookie sync (for reliable persistence)
        timers.scheduleTimeout(initInvoiceDataCookieSync, 500, 'invoice-cookie-sync');
        timers.scheduleTimeout(initInvoiceDataCookieSync, 1500, 'invoice-cookie-sync'); // Retry for late-rendering fields

        // Initialize invoice comuna filtering by region (v1.10.28)
        timers.scheduleTimeout(initInvoiceComunaFiltering, 600, 'invoice-comuna-filter');
        timers.scheduleTimeout(initInvoiceComunaFiltering, 1600, 'invoice-comuna-filter'); // Retry for late-rendering fields

        // Fix Region placeholder text (v1.10.33)
        initRegionPlaceholderObserver();

        // Listen for shipping changes to update time message
        document.addEventListener('change', (e) => {
            if (e.target.type === 'radio' && e.target.closest('.wc-block-components-shipping-rates-control')) {
                if (window.spcbShipping?.handleShippingChangeForTime) {
                    window.spcbShipping.handleShippingChangeForTime();
                }
            }
        });

        // v1.16.19: Accessibility improvements (direct setTimeout to avoid timer blocking)
        setTimeout(addSkipLink, 300);
        setTimeout(addSkipLink, 1000); // Retry for late-rendering
        setTimeout(enhanceAriaLabels, 500);
        setTimeout(enhanceAriaLabels, 1500); // Retry for late-rendering elements
    }

    let checkoutObserverActive = false;

    function observeCheckout() {
        if (checkoutObserverActive) return;
        checkoutObserverActive = true;

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    if (node.classList?.contains('wc-block-checkout') || node.querySelector?.('.wc-block-checkout')) {
                        // Found checkout, disconnect observer and init
                        observer.disconnect();
                        timers.scheduleTimeout(init, 100, 'checkout-observer-init');
                        return;
                    }
                }
            }
        });

        observer.observe(document.body, { childList: true, subtree: false });
    }

    // ============================================
    // ACCESSIBILITY - Skip Link and ARIA Labels
    // ============================================

    /**
     * Add skip link for keyboard navigation (v1.16.19)
     * Allows users to skip the sidebar and jump directly to the form
     * Uses keyboard listener to capture first Tab and show skip link
     */
    function addSkipLink() {
        if (document.querySelector('.spcb-skip-link')) return;

        const firstNameField = document.getElementById('contact-spcb-contact_first_name');
        if (!firstNameField) {
            log('Skip link: First name field not found');
            return;
        }

        const skipLink = document.createElement('a');
        skipLink.href = '#contact-spcb-contact_first_name';
        skipLink.className = 'spcb-skip-link';
        skipLink.textContent = 'Saltar al formulario';

        skipLink.addEventListener('click', (e) => {
            e.preventDefault();
            firstNameField.focus();
        });

        // Handle Enter/Space when focused
        skipLink.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                firstNameField.focus();
            }
        });

        document.body.insertBefore(skipLink, document.body.firstChild);
        log('Skip link added to DOM');

        // Capture first Tab press and redirect to skip link
        let skipLinkShown = false;
        function handleFirstTab(e) {
            if (e.key !== 'Tab' || skipLinkShown) return;

            // Only intercept if no input/form element has focus
            const activeEl = document.activeElement;
            const isFormElement = activeEl && (
                activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'SELECT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.tagName === 'BUTTON'
            );

            if (!isFormElement) {
                e.preventDefault();
                skipLink.focus();
                skipLinkShown = true;
                log('Skip link focused via Tab intercept');
            }
        }

        document.addEventListener('keydown', handleFirstTab, { capture: true });

        // Remove listener after skip link loses focus
        skipLink.addEventListener('blur', () => {
            if (skipLinkShown) {
                document.removeEventListener('keydown', handleFirstTab, { capture: true });
                log('Skip link Tab intercept removed');
            }
        });
    }

    /**
     * Enhance ARIA labels for better screen reader support
     */
    function enhanceAriaLabels() {
        // Header links
        const logo = document.querySelector('.spcb-store-logo');
        if (logo && !logo.hasAttribute('aria-label')) {
            logo.setAttribute('aria-label', 'Ir a la tienda');
        }

        const whatsapp = document.querySelector('.spcb-contact-whatsapp');
        if (whatsapp && !whatsapp.hasAttribute('aria-label')) {
            whatsapp.setAttribute('aria-label', 'Contactar por WhatsApp');
        }

        const emailLink = document.querySelector('.spcb-contact-email');
        if (emailLink && !emailLink.hasAttribute('aria-label')) {
            emailLink.setAttribute('aria-label', 'Enviar correo electrónico');
        }

        // Order summary
        const summary = document.querySelector('.wc-block-components-order-summary');
        if (summary && !summary.hasAttribute('aria-label')) {
            summary.setAttribute('role', 'region');
            summary.setAttribute('aria-label', 'Resumen del pedido');
        }

        // Footer contact
        const footerContact = document.getElementById('spcb-footer-contact');
        if (footerContact && !footerContact.hasAttribute('aria-label')) {
            footerContact.setAttribute('role', 'contentinfo');
            footerContact.setAttribute('aria-label', 'Información de contacto');
        }

        log('ARIA labels enhanced');
    }

    // Start
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { init(); observeCheckout(); });
    } else {
        init();
        observeCheckout();
    }

    // Expose for other modules and debugging
    window.spcbDocument = {
        init,
        initDocumentType,
        updateDocumentTypeClass,
        toggleInvoiceFields,
        findDocumentTypeSelect,
        prefillFieldsFromPreferences,
        saveInvoiceDataToCookie,
        clearWCBlocksValidationErrors,
        REGION_NAMES
    };

    log('SPCB Document Module v1.8.0 loaded');
})();
